import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Building2, Globe2 } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import useJsonLd from "@/hooks/useJsonLd";
import ContourBackground from "@/components/ContourBackground";
import Reveal from "@/components/Reveal";
import { US_STATES, US_REGIONS, METROS } from "@/data/usLocations";
import { PRIMARY_KEYWORDS, composeKeywords } from "@/data/seoKeywords";
import { locationsIndexLd, breadcrumb } from "@/lib/jsonLd";

export default function LocationsIndex() {
  useSeo({
    title: "CAD Drafting Coverage — All 50 States, 280+ Counties · Tri-Axis XYZ",
    description:
      "Nationwide CAD drafting and land-survey CAD outsourcing. Browse all 50 US states, ~280 counties and 25 metro hubs we serve for ALTA, boundary, topo, plats, MEP/BIM, structural, and civil drafting.",
  });

  useJsonLd(locationsIndexLd(), "locations-index");
  useJsonLd(
    breadcrumb([
      { name: "Home", path: "/" },
      { name: "Coverage", path: "/cad-services" },
    ]),
    "locations-index-bc",
  );

  return (
    <div data-testid="page-locations-index">
      <section className="relative overflow-hidden border-b border-slate-200 bg-paper-dark/40">
        <ContourBackground variant="light" opacity={0.5} seed={29} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
            Coverage · United States · 50 States · 280+ Counties
          </div>
          <h1 className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 max-w-4xl">
            CAD drafting & land-survey CAD,{" "}
            <span className="text-emerald-700">delivered nationwide.</span>
          </h1>
          <p className="mt-5 max-w-3xl text-base sm:text-lg text-slate-600 leading-relaxed">
            From <strong>Manhattan to Maricopa County</strong>, Tri-Axis XYZ has
            production teams ready for any US firm. Pick a state to see active
            counties, ZIP coverage and the CAD disciplines we run there — ALTA,
            boundary, topo, plats, ROW, GIS, architectural, structural, MEP/BIM,
            mechanical, civil and 3D.
          </p>
          <div className="mt-7 flex flex-wrap gap-2">
            {PRIMARY_KEYWORDS.slice(0, 7).map((k) => (
              <span
                key={k}
                className="mono text-[10px] tracking-wide uppercase border border-slate-300 bg-white text-slate-700 px-2.5 py-1"
              >
                {k}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Region groups */}
      <section className="bg-white border-b border-slate-200 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {US_REGIONS.map((region) => {
            const states = US_STATES.filter((s) => s.region === region);
            return (
              <Reveal key={region} id={`region-${region.toLowerCase()}`} className="mb-14 last:mb-0">
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div>
                    <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
                      Region
                    </div>
                    <h2 className="display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mt-1">
                      {region} · {states.length} states
                    </h2>
                  </div>
                  <Globe2 className="h-7 w-7 text-emerald-600" />
                </div>
                <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {states.map((s) => (
                    <li key={s.slug}>
                      <Link
                        to={`/cad-services/${s.slug}`}
                        data-testid={`state-link-${s.slug}`}
                        className="group flex items-center justify-between border border-slate-200 bg-white hover:border-emerald-600 hover:bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-slate-800 transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <span className="mono text-[10px] text-slate-500 group-hover:text-emerald-700">
                            {s.abbr}
                          </span>
                          {s.state}
                        </span>
                        <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all text-emerald-600" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Metro hubs */}
      <section id="metro-hubs" className="bg-paper-dark/30 border-b border-slate-200 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              Metro Hubs
            </div>
            <h2 className="display mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
              25 metros where we run most projects
            </h2>
            <p className="mt-3 max-w-2xl text-slate-600">
              High-density CAD-drafting demand centers. Click a metro for
              anchor counties and a dedicated quote shortcut.
            </p>
          </Reveal>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {METROS.map((m) => (
              <li key={m.slug}>
                <Link
                  to={`/metros/${m.slug}`}
                  data-testid={`metro-link-${m.slug}`}
                  className="group block border border-slate-200 bg-white hover:border-emerald-600 hover:shadow-sm transition p-4"
                >
                  <div className="flex items-center gap-2 text-emerald-700">
                    <Building2 className="h-4 w-4" />
                    <span className="mono text-[10px] tracking-[0.18em] uppercase">
                      {m.state} Metro
                    </span>
                  </div>
                  <div className="mt-1 font-extrabold text-slate-900">{m.name}</div>
                  <div className="mt-1 text-xs text-slate-600 leading-snug line-clamp-2">
                    {m.anchor}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Hidden but indexable keyword cluster */}
      <section className="bg-white py-12 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              <MapPin className="inline h-3 w-3 -mt-0.5 mr-1" />
              Local SEO keyword cluster
            </div>
            <p className="mt-3 text-sm text-slate-500 leading-relaxed">
              {composeKeywords(PRIMARY_KEYWORDS).slice(0, 1400)}…
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
